import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import api from 'api';
import { useNavigate } from '@tanstack/react-router';
import NumberedDataGridPagination from 'components/NumberedDataGridPagination';

const NewReportDialog = ({ handleClose }) => {
    const navigate = useNavigate();
    const visitID = useSelector((state) => state.chart.visit_id);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState({ type: 'include', ids: new Set() });
    const [title, setTitle] = useState('');

    const columns = [{ field: 'template_name', headerName: 'Template', flex: 1, minWidth: 240, sortable: false }];

    const handleSetTitle = (e) => {
        setTitle(e.target.value);
    };

    const handleCreateReport = async () => {
        try {
            const selectedTemplateId = selected.ids.values().next().value;
            const selectedTemplate = rows.find((row) => row.template_id === selectedTemplateId);
            if (!selectedTemplate) {
                return;
            }

            const result = await api.post('/reports', {
                report_name: title,
                template_id: selectedTemplate.template_id,
                visit_id: visitID,
            });

            navigate({ to: `/reports/edit/${result.data.urpt_id}` });
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        setLoading(true);
        api.get('/reports/templates', {
            params: {
                page_size: pageSize,
                page_number: page,
            },
        })
            .then((res) => {
                const { templates } = res.data;
                setRows(templates || []);
                setTotal(templates[0]?.total_records || 0);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [page, pageSize, visitID]);

    return (
        <Dialog fullWidth maxWidth="lg" open onClose={handleClose}>
            <DialogTitle>Create Report</DialogTitle>

            <DialogContent>
                <div>Select Template:</div>

                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowCount={total}
                    paginationMode="server"
                    paginationModel={{ page, pageSize }}
                    onPaginationModelChange={(model) => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                    }}
                    rowSelectionModel={selected}
                    onRowSelectionModelChange={(newSelection) => setSelected(newSelection)}
                    getRowId={(row) => row.template_id}
                    pageSizeOptions={[10, 20, 50, 100]}
                    disableColumnMenu
                    disableCellFocus
                    sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },
                    }}
                    slots={{ pagination: NumberedDataGridPagination }}
                    autoHeight
                />

                <div>Enter Report Title:</div>
                <div>
                    <TextField
                        placeholder="Report Title"
                        inputProps={{ maxLength: 100 }}
                        value={title}
                        onChange={handleSetTitle}
                    />
                </div>

                <div>
                    Upon clicking Create Report, you will be taken to your new report. If you have unsaved work on this
                    chart, click cancel and save your work before creating a report.
                </div>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleCreateReport}
                    variant="contained"
                    color="primary"
                    disabled={title.trim().length === 0 || loading || selected.ids.size === 0}
                >
                    Create Report
                </Button>

                <Button onClick={handleClose} variant="outlined">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewReportDialog;
